import { useState, useEffect } from 'react';
import { useCompany } from '../../context/CompanyContext';
import { auth1Api } from '../../services/api';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import './AuthActivity.css';

const AuthActivity = () => {
  const { currentCompany, isCompanyView } = useCompany();
  const [loading, setLoading] = useState(true);
  const [apiConnected, setApiConnected] = useState(false);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('activity');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    checkApiStatus();
    fetchAuthData();
    // Set up polling for real-time updates
    const interval = setInterval(fetchAuthData, 30000);
    return () => clearInterval(interval);
  }, [currentCompany]);

  const checkApiStatus = async () => {
    try {
      const status = await auth1Api.status();
      setApiConnected(status.connected);
    } catch (err) {
      console.warn('Auth1 API not connected:', err.message);
      setApiConnected(false);
    }
  };

  const getTenantId = () => {
    // Get tenant ID from current company or use a default for testing
    if (isCompanyView && currentCompany) {
      return currentCompany.auth1TenantId || currentCompany.id || currentCompany.slug;
    }
    // For the "all companies" view, we'd need to aggregate or use a super-admin tenant
    return null;
  };

  const fetchAuthData = async () => {
    setLoading(true);
    const tenantId = getTenantId();

    try {
      if (apiConnected && tenantId) {
        // Fetch real data from Auth1 API
        const [dashboardData, auditLogs, sessionsData, usersData] = await Promise.all([
          auth1Api.getDashboard(tenantId).catch(() => null),
          auth1Api.getAuditLogs(tenantId, { limit: 50 }).catch(() => ({ logs: [] })),
          auth1Api.listSessions(tenantId, { limit: 50 }).catch(() => ({ sessions: [] })),
          auth1Api.listUsers(tenantId, { limit: 50 }).catch(() => ({ users: [] }))
        ]);

        if (dashboardData) {
          setStats({
            totalUsers: parseInt(dashboardData.users?.total_users || 0),
            activeUsers: parseInt(dashboardData.users?.active_users || 0),
            newUsersToday: parseInt(dashboardData.users?.new_users_today || 0),
            activeSessions: parseInt(dashboardData.sessions?.active_sessions || 0),
            loginsToday: parseInt(dashboardData.audit?.successful_logins || 0),
            failedLogins: parseInt(dashboardData.audit?.failed_logins || 0),
            fraudAlerts: dashboardData.securityAlerts?.length || 0,
            mfaEnabled: 0 // Would need separate query
          });
        }

        // Transform audit logs to events format
        setEvents(auditLogs.logs.map(log => ({
          id: log.id,
          type: mapEventType(log.event_type),
          user: log.email || log.phone_number || log.user_id || 'Unknown',
          ip: log.ip_address || 'Unknown',
          location: 'Unknown', // Would need geo-IP lookup
          time: formatTimeAgo(log.created_at),
          risk: getRiskLevel(log.event_type)
        })));

        // Transform sessions
        setSessions(sessionsData.sessions.map(session => ({
          id: session.id,
          user: session.email || session.phone_number || session.user_id,
          device: parseUserAgent(session.user_agent),
          ip: session.ip_address || 'Unknown',
          started: formatTimeAgo(session.created_at),
          lastActive: formatTimeAgo(session.created_at)
        })));

        // Transform users
        setUsers(usersData.users.map(user => ({
          id: user.id,
          email: user.email || user.phone_number,
          name: user.display_name || user.email || user.phone_number,
          status: user.status || 'active',
          mfa: false, // Would need to check MFA status
          lastLogin: user.last_login_at ? formatTimeAgo(user.last_login_at) : 'Never',
          role: 'User'
        })));

      } else {
        // Use mock data when API is not connected
        loadMockData();
      }
    } catch (error) {
      console.error('Failed to fetch auth data:', error);
      // Fall back to mock data on error
      loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const loadMockData = () => {
    setStats({
      totalUsers: 2847,
      activeUsers: 1892,
      newUsersToday: 47,
      activeSessions: 1243,
      loginsToday: 3421,
      failedLogins: 23,
      fraudAlerts: 5,
      mfaEnabled: 78
    });

    setEvents([
      { id: 1, type: 'login_success', user: 'john@example.com', ip: '192.168.1.100', location: 'New York, US', time: '2 min ago', risk: 'low' },
      { id: 2, type: 'login_success', user: 'sarah@company.com', ip: '10.0.0.45', location: 'Los Angeles, US', time: '5 min ago', risk: 'low' },
      { id: 3, type: 'login_failed', user: 'admin@test.com', ip: '203.45.67.89', location: 'Unknown', time: '8 min ago', risk: 'high' },
      { id: 4, type: 'mfa_enabled', user: 'mike@startup.io', ip: '172.16.0.1', location: 'Chicago, US', time: '12 min ago', risk: 'low' },
      { id: 5, type: 'password_reset', user: 'emily@corp.com', ip: '192.168.2.50', location: 'Seattle, US', time: '15 min ago', risk: 'medium' },
      { id: 6, type: 'fraud_alert', user: 'suspicious@temp.com', ip: '45.67.89.12', location: 'Russia', time: '20 min ago', risk: 'critical' },
      { id: 7, type: 'session_revoked', user: 'alex@domain.com', ip: '10.1.1.1', location: 'Austin, US', time: '25 min ago', risk: 'low' },
      { id: 8, type: 'login_success', user: 'rachel@tech.co', ip: '192.168.3.100', location: 'Boston, US', time: '30 min ago', risk: 'low' },
    ]);

    setSessions([
      { id: 1, user: 'john@example.com', device: 'Chrome on MacOS', ip: '192.168.1.100', started: '2h ago', lastActive: '2 min ago' },
      { id: 2, user: 'sarah@company.com', device: 'Safari on iOS', ip: '10.0.0.45', started: '1h ago', lastActive: '5 min ago' },
      { id: 3, user: 'mike@startup.io', device: 'Firefox on Windows', ip: '172.16.0.1', started: '4h ago', lastActive: '12 min ago' },
      { id: 4, user: 'emily@corp.com', device: 'Chrome on Android', ip: '192.168.2.50', started: '30 min ago', lastActive: '15 min ago' },
    ]);

    setUsers([
      { id: 1, email: 'john@example.com', name: 'John Smith', status: 'active', mfa: true, lastLogin: '2 min ago', role: 'Admin' },
      { id: 2, email: 'sarah@company.com', name: 'Sarah Johnson', status: 'active', mfa: true, lastLogin: '5 min ago', role: 'User' },
      { id: 3, email: 'mike@startup.io', name: 'Mike Chen', status: 'active', mfa: false, lastLogin: '12 min ago', role: 'User' },
      { id: 4, email: 'emily@corp.com', name: 'Emily Davis', status: 'pending', mfa: false, lastLogin: 'Never', role: 'User' },
      { id: 5, email: 'alex@domain.com', name: 'Alex Brown', status: 'blocked', mfa: true, lastLogin: '2 days ago', role: 'User' },
    ]);
  };

  // Helper functions for data transformation
  const mapEventType = (eventType) => {
    const mapping = {
      'auth.login': 'login_success',
      'auth.failed': 'login_failed',
      'auth.logout': 'session_revoked',
      'auth.request': 'login_attempt',
      'auth.verify_code': 'mfa_verify',
      'user.created': 'user_created',
      'user.blocked': 'user_blocked',
      'session.created': 'login_success',
      'session.revoked': 'session_revoked',
      'security.high_risk': 'fraud_alert',
      'security.suspicious_activity': 'fraud_alert',
      'security.voip_blocked': 'voip_blocked',
      'security.rate_limited': 'rate_limited'
    };
    return mapping[eventType] || eventType?.replace('.', '_') || 'unknown';
  };

  const getRiskLevel = (eventType) => {
    if (eventType?.includes('security.high_risk')) return 'critical';
    if (eventType?.includes('security.suspicious')) return 'high';
    if (eventType?.includes('auth.failed')) return 'medium';
    if (eventType?.includes('security')) return 'medium';
    return 'low';
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return `${seconds} sec ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  const parseUserAgent = (ua) => {
    if (!ua) return 'Unknown Device';
    // Simple parsing - could be enhanced with a library
    if (ua.includes('Chrome')) return 'Chrome on ' + (ua.includes('Mac') ? 'MacOS' : ua.includes('Windows') ? 'Windows' : 'Linux');
    if (ua.includes('Safari')) return 'Safari on ' + (ua.includes('iPhone') ? 'iOS' : 'MacOS');
    if (ua.includes('Firefox')) return 'Firefox on ' + (ua.includes('Windows') ? 'Windows' : 'Linux');
    return ua.substring(0, 30);
  };

  const handleBlockUser = async (userId) => {
    const tenantId = getTenantId();
    if (!tenantId) return;

    try {
      await auth1Api.blockUser(tenantId, userId, 'Blocked by admin');
      fetchAuthData(); // Refresh data
    } catch (error) {
      console.error('Failed to block user:', error);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    const tenantId = getTenantId();
    if (!tenantId) return;

    try {
      await auth1Api.revokeSession(tenantId, sessionId);
      fetchAuthData(); // Refresh data
    } catch (error) {
      console.error('Failed to revoke session:', error);
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'login_success': return 'log-in';
      case 'login_failed': return 'x-circle';
      case 'mfa_enabled': return 'shield';
      case 'password_reset': return 'key';
      case 'fraud_alert': return 'alert-triangle';
      case 'session_revoked': return 'log-out';
      default: return 'activity';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'var(--danger)';
      case 'high': return 'var(--danger)';
      case 'medium': return 'var(--warning)';
      default: return 'var(--success)';
    }
  };

  const filteredEvents = events.filter(e => {
    if (filter === 'all') return true;
    if (filter === 'success') return e.type === 'login_success';
    if (filter === 'failed') return e.type === 'login_failed';
    if (filter === 'alerts') return e.risk === 'high' || e.risk === 'critical';
    return true;
  });

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers?.toLocaleString() || '0', change: `+${stats?.newUsersToday || 0} today`, changeType: 'positive' },
    { label: 'Active Sessions', value: stats?.activeSessions?.toLocaleString() || '0', change: 'Real-time', changeType: 'neutral' },
    { label: 'Logins Today', value: stats?.loginsToday?.toLocaleString() || '0', change: `${stats?.failedLogins || 0} failed`, changeType: stats?.failedLogins > 10 ? 'warning' : 'positive' },
    { label: 'Fraud Alerts', value: stats?.fraudAlerts?.toString() || '0', change: stats?.fraudAlerts > 0 ? 'Action needed' : 'All clear', changeType: stats?.fraudAlerts > 0 ? 'warning' : 'positive' },
  ];

  return (
    <div className="auth-activity">
      <div className="auth-header">
        <div className="auth-header-left">
          <h1 className="auth-title">Auth1 Activity</h1>
          <p className="auth-subtitle">
            {isCompanyView ? `Monitoring ${currentCompany?.name || 'Company'}` : 'All companies authentication activity'}
            <span className={`api-status ${apiConnected ? 'connected' : 'disconnected'}`}>
              <Icon name={apiConnected ? 'check-circle' : 'alert-circle'} />
              {apiConnected ? 'Live Data' : 'Demo Mode'}
            </span>
          </p>
        </div>
        <div className="auth-header-right">
          <Button variant="secondary" icon="download">Export Logs</Button>
          <Button variant="primary" icon="refresh" onClick={fetchAuthData} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      <div className="auth-stats">
        {statCards.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loading} />
        ))}
      </div>

      <div className="auth-tabs">
        <button
          className={`auth-tab ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          <Icon name="activity" /> Activity Feed
        </button>
        <button
          className={`auth-tab ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          <Icon name="users" /> Active Sessions
        </button>
        <button
          className={`auth-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Icon name="user" /> User Management
        </button>
      </div>

      {activeTab === 'activity' && (
        <div className="auth-section">
          <div className="auth-section-header">
            <h3>Real-time Activity</h3>
            <div className="auth-filters">
              <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
              <button className={`filter-btn ${filter === 'success' ? 'active' : ''}`} onClick={() => setFilter('success')}>Success</button>
              <button className={`filter-btn ${filter === 'failed' ? 'active' : ''}`} onClick={() => setFilter('failed')}>Failed</button>
              <button className={`filter-btn ${filter === 'alerts' ? 'active' : ''}`} onClick={() => setFilter('alerts')}>Alerts</button>
            </div>
          </div>
          <div className="activity-feed">
            {loading ? (
              <div className="loading-state">Loading activity...</div>
            ) : filteredEvents.map(event => (
              <div key={event.id} className={`activity-event ${event.risk}`}>
                <div className="event-icon" style={{ color: getRiskColor(event.risk) }}>
                  <Icon name={getEventIcon(event.type)} />
                </div>
                <div className="event-details">
                  <div className="event-main">
                    <span className="event-type">{event.type.replace('_', ' ')}</span>
                    <span className="event-user">{event.user}</span>
                  </div>
                  <div className="event-meta">
                    <span><Icon name="globe" /> {event.location}</span>
                    <span><Icon name="monitor" /> {event.ip}</span>
                    <span><Icon name="clock" /> {event.time}</span>
                  </div>
                </div>
                {(event.risk === 'high' || event.risk === 'critical') && (
                  <button className="event-action">
                    <Icon name="shield" /> Block
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="auth-section">
          <div className="auth-section-header">
            <h3>Active Sessions ({sessions.length})</h3>
            <Button variant="danger" size="sm" icon="log-out">Revoke All</Button>
          </div>
          <div className="sessions-list">
            {sessions.map(session => (
              <div key={session.id} className="session-item">
                <div className="session-info">
                  <div className="session-user">{session.user}</div>
                  <div className="session-device">{session.device}</div>
                  <div className="session-meta">
                    <span>IP: {session.ip}</span>
                    <span>Started: {session.started}</span>
                    <span>Last active: {session.lastActive}</span>
                  </div>
                </div>
                <button className="session-revoke" onClick={() => handleRevokeSession(session.id)}>
                  <Icon name="x" /> Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="auth-section">
          <div className="auth-section-header">
            <h3>Users ({users.length})</h3>
            <Button variant="primary" size="sm" icon="user-plus">Add User</Button>
          </div>
          <div className="users-table">
            <div className="users-table-header">
              <span>User</span>
              <span>Status</span>
              <span>MFA</span>
              <span>Last Login</span>
              <span>Role</span>
              <span>Actions</span>
            </div>
            {users.map(user => (
              <div key={user.id} className="users-table-row">
                <div className="user-info">
                  <span className="user-name">{user.name}</span>
                  <span className="user-email">{user.email}</span>
                </div>
                <span className={`user-status ${user.status}`}>{user.status}</span>
                <span className={`user-mfa ${user.mfa ? 'enabled' : 'disabled'}`}>
                  {user.mfa ? 'Enabled' : 'Disabled'}
                </span>
                <span className="user-login">{user.lastLogin}</span>
                <span className="user-role">{user.role}</span>
                <div className="user-actions">
                  <button className="action-btn" title="Edit"><Icon name="edit-2" /></button>
                  <button
                    className="action-btn"
                    title={user.status === 'blocked' ? 'Unblock' : 'Block'}
                    onClick={() => handleBlockUser(user.id)}
                  >
                    <Icon name={user.status === 'blocked' ? 'check-circle' : 'slash'} />
                  </button>
                  <button className="action-btn danger" title="Delete"><Icon name="trash-2" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthActivity;
